/**
 * selection — sélection de zone sur un calque image.
 *
 * Quatre formes : rectangle, ellipse, lasso libre, lasso polygonal. La zone
 * délimitée sert ensuite d'emporte-pièce sur le masque de gomme du calque.
 *
 * POURQUOI ÇA RÉUTILISE LA GOMME PLUTÔT QUE D'INVENTER UN CHEMIN
 * `el.eraseMask` est déjà un masque alpha couvrant la boîte du calque, déjà
 * honoré à l'écran (CSS `mask-image`) ET par les trois chemins d'export
 * (`flattenEraseMasks` aplatit avant l'envoi). Une sélection effacée n'est donc
 * rien d'autre qu'un coup de gomme à bord droit : zéro nouveau rendu à écrire,
 * zéro risque de voir les pixels réapparaître à l'impression, et l'affaire
 * reste réversible (« Tout restaurer » de la barre de gomme).
 *
 * DANS QUEL REPÈRE VIT UNE SÉLECTION
 * En coordonnées NORMALISÉES 0..1 de la boîte du calque, et du côté NON
 * RETOURNÉ — c'est-à-dire le repère du masque, pas celui de l'écran. Deux
 * raisons : le masque s'étire en `mask-size: 100% 100%` sur cette même boîte
 * (donc u,v → pixels du masque est une simple multiplication), et une sélection
 * survit ainsi au zoom, au redimensionnement du calque et à sa rotation.
 *
 * Conséquence à connaître : le retournement (`scaleX: -1`) est appliqué par CSS
 * sur la balise image, APRÈS le masquage. Le repère du masque est donc celui de
 * l'image non retournée, et l'affichage du contour doit refaire la conversion
 * en sens inverse (cf. `cheminSVG`). Si l'utilisateur retourne son image après
 * coup, la sélection suit les pixels — ce qui est le comportement attendu quand
 * on vise une zone de l'image, et non une zone de la page.
 *
 * LA CONTRAINTE SHIFT SE CALCULE EN MILLIMÈTRES
 * Un carré doit être carré à l'œil, pas dans l'espace normalisé. Sur un calque
 * de 80 × 20 mm, contraindre du/dv à égalité donnerait un rectangle de 80 × 20 :
 * proportionnel à la boîte, et parfaitement faux. Tout ce qui touche à Shift
 * passe donc par les millimètres du document.
 */
import { openMaskCanvas } from './eraseMask';

/** Les quatre formes, dans l'ordre où la touche M les fait défiler. */
export const FORMES = ['rect', 'ellipse', 'lasso', 'polygon'];

export const NOMS_FORMES = {
    rect: 'Rectangle',
    ellipse: 'Ellipse',
    lasso: 'Lasso libre',
    polygon: 'Lasso polygonal',
};

/**
 * Point écran → coordonnées normalisées du masque du calque.
 *
 * Reprend la rotation inverse autour du centre de la boîte, puis le
 * retournement en dernier. Partagé avec la gomme (`EraserSurface`) : cette
 * arithmétique s'était déjà trompée une fois, elle ne doit exister qu'ici.
 *
 * @param {object} el     calque (width/height en mm, rotation en degrés)
 * @param {number} scale  échelle d'affichage, en pixels écran par millimètre
 * @param {DOMRect} rect  boîte écran du conteneur du calque
 */
export function coordsBoite(el, scale, rect, clientX, clientY) {
    // La bbox d'un rectangle tourné autour de son centre garde ce centre.
    const dx = clientX - (rect.left + rect.width / 2);
    const dy = clientY - (rect.top + rect.height / 2);
    const a = -((el.rotation || 0) * Math.PI) / 180;
    const lx = dx * Math.cos(a) - dy * Math.sin(a);
    const ly = dx * Math.sin(a) + dy * Math.cos(a);

    let u = 0.5 + lx / Math.max(1e-6, el.width * scale);
    let v = 0.5 + ly / Math.max(1e-6, el.height * scale);
    if (el.scaleX === -1) u = 1 - u;
    if (el.scaleY === -1) v = 1 - v;
    return { u, v };
}

/**
 * Bride une coordonnée à un domaine RAISONNABLE — et non à la boîte du calque.
 *
 * POURQUOI ON LAISSE LA SÉLECTION DÉBORDER, ce qui n'est pas l'évidence.
 * Borner à [0, 1] paraissait plus propre. Mesuré, c'était faux : avec un bord
 * adouci, une zone bornée au bord du calque fait tomber l'alpha à 138 sur 255
 * au ras de ce bord (relevé sur le banc d'essai). Le fondu se centre sur la
 * frontière de la zone ; si cette frontière est le bord de l'image, la moitié du
 * dégradé mord dans les pixels à garder, et un « effacer dehors » censé recadrer
 * proprement mange une frange sur ses quatre côtés.
 *
 * Laisser le geste dépasser règle le problème à la racine, pour les quatre
 * formes à la fois : le dégradé se centre alors HORS du calque, et le bord reste
 * net. C'est aussi ce que fait Photoshop — on peut y tirer une sélection
 * au-delà du bord du document. Le contour affiché, lui, est rogné par la boîte
 * du <svg>, qui masque son débordement sans qu'on ait rien à écrire.
 *
 * Le domaine reste borné à [-1, 2] : au-delà, un geste parti très loin ne
 * produirait que des chemins démesurés, sans rien changer au résultat.
 */
export function brider({ u, v }) {
    return { u: Math.min(2, Math.max(-1, u)), v: Math.min(2, Math.max(-1, v)) };
}

/**
 * Contraint le second coin pour que la zone soit un CARRÉ réel (Shift).
 * Le côté retenu est le plus grand des deux, mesuré en millimètres, et le sens
 * du geste est préservé — tirer vers le haut à gauche reste possible.
 */
export function contraindreCarre(ancre, point, el) {
    const w = Math.max(0.01, el.width);
    const h = Math.max(0.01, el.height);
    const dxMm = (point.u - ancre.u) * w;
    const dyMm = (point.v - ancre.v) * h;
    const cote = Math.max(Math.abs(dxMm), Math.abs(dyMm));
    return {
        u: ancre.u + (Math.sign(dxMm) || 1) * cote / w,
        v: ancre.v + (Math.sign(dyMm) || 1) * cote / h,
    };
}

/**
 * Contraint un segment de lasso polygonal aux multiples de 45° (Shift), là
 * encore mesurés en millimètres pour que l'angle soit vrai à l'écran.
 */
export function contraindre45(ancre, point, el) {
    const w = Math.max(0.01, el.width);
    const h = Math.max(0.01, el.height);
    const dxMm = (point.u - ancre.u) * w;
    const dyMm = (point.v - ancre.v) * h;
    const d = Math.hypot(dxMm, dyMm);
    if (d < 1e-6) return point;
    const pas = Math.PI / 4;
    const angle = Math.round(Math.atan2(dyMm, dxMm) / pas) * pas;
    return {
        u: ancre.u + (Math.cos(angle) * d) / w,
        v: ancre.v + (Math.sin(angle) * d) / h,
    };
}

/**
 * MAGNÉTISME DU TRACÉ SUR LES REPÈRES.
 *
 * Un coin de zone doit pouvoir se poser EXACTEMENT sur un repère, comme un
 * calque qu'on glisse. Sans ça, découper au ras d'un repère se fait à l'œil et
 * au pixel près, ce qui est précisément ce qu'un repère est censé éviter.
 *
 * DEUX REPÈRES À NE PAS CONFONDRE. La zone vit en coordonnées normalisées du
 * MASQUE (0..1, côté non retourné — cf. l'en-tête de ce fichier) ; un repère vit
 * en MILLIMÈTRES du document. La conversion passe donc par la fraction VISUELLE
 * le long de la boîte, celle qui se lit à l'écran de gauche à droite : c'est elle
 * — et non `u` — qui correspond à `el.x + fraction × el.width`. Sur un calque
 * retourné les deux diffèrent, et confondre les deux collait la zone au repère
 * opposé.
 *
 * UN CALQUE PIVOTÉ N'EST PAS AIMANTÉ, et c'est un choix. Les axes de la zone
 * suivent alors le calque, pas la page : « poser un coin sur un repère
 * horizontal » n'y désigne plus une valeur de `v` unique mais une diagonale.
 * Aimanter un seul axe y donnerait un résultat faux et inexplicable ; on rend la
 * main au geste libre.
 *
 * @param {object} p         point normalisé du masque {u, v}
 * @param {object} el        calque cible
 * @param {number} scale     pixels écran par millimètre (pour un seuil constant à l'écran)
 * @param {number[]} guidesX abscisses des repères verticaux, en mm
 * @param {number[]} guidesY ordonnées des repères horizontaux, en mm
 */
export function aimanterAuxReperes(p, el, scale, guidesX, guidesY) {
    if (!el || (el.rotation || 0) !== 0) return p;
    const w = el.width || 0;
    const h = el.height || 0;
    if (w <= 0 || h <= 0) return p;

    // Même seuil que le glissement d'un calque (cf. LabelCanvas) : 5 px écran.
    const seuil = 5 / Math.max(0.01, scale);
    const fx = el.scaleX === -1;
    const fy = el.scaleY === -1;

    // fraction visuelle → millimètres du document, et retour.
    const versMm = (t, debut, taille) => debut + t * taille;
    const plusProche = (valeurMm, cibles) => {
        let best = null, dist = seuil;
        for (const c of cibles) {
            const d = Math.abs(valeurMm - c);
            if (d < dist) { dist = d; best = c; }
        }
        return best;
    };

    let { u, v } = p;

    const uVis = fx ? 1 - u : u;
    const gx = plusProche(versMm(uVis, el.x, w), guidesX || []);
    if (gx !== null) {
        const t = (gx - el.x) / w;
        u = fx ? 1 - t : t;
    }

    const vVis = fy ? 1 - v : v;
    const gy = plusProche(versMm(vVis, el.y, h), guidesY || []);
    if (gy !== null) {
        const t = (gy - el.y) / h;
        v = fy ? 1 - t : t;
    }

    return { u, v };
}

/**
 * Repères aimantables du document : ceux posés par l'utilisateur, plus les axes
 * centraux quand ils sont affichés. Exactement l'ensemble que retient le
 * glissement d'un calque — les deux gestes doivent accrocher les mêmes traits,
 * sous peine d'un magnétisme qui « marche parfois ».
 */
export function reperesDuDocument(elements, canvas) {
    const x = [];
    const y = [];
    for (const e of elements || []) {
        if (e.type !== 'guide') continue;
        if (e.guideType === 'vertical') x.push(e.x);
        else y.push(e.y);
    }
    if (canvas?.showGuides) {
        x.push((canvas.width_mm || 0) / 2);
        y.push((canvas.height_mm || 0) / 2);
    }
    return { x, y };
}

/** Boîte englobante normalisée d'une sélection. */
export function boiteMarquee(marquee) {
    const pts = marquee?.points || [];
    if (!pts.length) return null;
    let u0 = Infinity, v0 = Infinity, u1 = -Infinity, v1 = -Infinity;
    for (const [u, v] of pts) {
        if (u < u0) u0 = u;
        if (v < v0) v0 = v;
        if (u > u1) u1 = u;
        if (v > v1) v1 = v;
    }
    return { u0, v0, u1, v1 };
}

/**
 * Dimensions en millimètres d'une sélection, pour affichage dans la barre.
 * Bornées à la boîte du calque : la sélection peut légitimement déborder (cf.
 * `brider`), mais annoncer 112 mm sur un calque de 90 mm n'aiderait personne —
 * ce qui compte est la zone réellement effacée.
 */
export function tailleMm(marquee, el) {
    const b = boiteMarquee(marquee);
    if (!b || !el) return null;
    const u0 = Math.max(0, Math.min(1, b.u0)), u1 = Math.max(0, Math.min(1, b.u1));
    const v0 = Math.max(0, Math.min(1, b.v0)), v1 = Math.max(0, Math.min(1, b.v1));
    return {
        w: Math.abs(u1 - u0) * (el.width || 0),
        h: Math.abs(v1 - v0) * (el.height || 0),
    };
}

/** Une sélection assez grande pour valoir un effacement ? */
export function marqueeUtile(marquee) {
    if (!marquee?.points?.length) return false;
    if (marquee.shape === 'lasso' || marquee.shape === 'polygon') {
        if (marquee.points.length < 3) return false;
    }
    const b = boiteMarquee(marquee);
    // 0,2 % de la boîte : sous ce seuil, c'est un clic, pas un tracé.
    return b && Math.abs(b.u1 - b.u0) > 0.002 && Math.abs(b.v1 - b.v0) > 0.002;
}

/**
 * Chemin SVG du contour, en pixels écran du conteneur du calque.
 *
 * Le conteneur n'étant PAS retourné (le `scaleX(-1)` vit sur la balise image,
 * cf. ImageLayer), il faut défaire ici le retournement que `coordsBoite` a
 * appliqué à l'aller. Sans ça, le contour se dessine en miroir de la zone
 * réellement effacée sur une image retournée.
 *
 * @param {number} W largeur écran du conteneur, en pixels
 * @param {number} H hauteur écran du conteneur, en pixels
 */
export function cheminSVG(marquee, W, H, el) {
    const pts = marquee?.points || [];
    if (!pts.length) return '';
    const fx = el?.scaleX === -1;
    const fy = el?.scaleY === -1;
    const X = (u) => (fx ? 1 - u : u) * W;
    const Y = (v) => (fy ? 1 - v : v) * H;

    if (marquee.shape === 'rect' || marquee.shape === 'ellipse') {
        const [[u0, v0], [u1, v1]] = [pts[0], pts[pts.length - 1]];
        const x0 = X(u0), y0 = Y(v0), x1 = X(u1), y1 = Y(v1);
        const left = Math.min(x0, x1), top = Math.min(y0, y1);
        const w = Math.abs(x1 - x0), h = Math.abs(y1 - y0);
        if (marquee.shape === 'rect') {
            return `M ${left} ${top} H ${left + w} V ${top + h} H ${left} Z`;
        }
        // Ellipse en deux arcs : un seul nœud <path> dont on réécrit `d`
        // pendant le geste, sans jamais recréer d'élément.
        const rx = w / 2, ry = h / 2;
        const cx = left + rx, cy = top + ry;
        return `M ${cx - rx} ${cy} A ${rx} ${ry} 0 1 0 ${cx + rx} ${cy} `
             + `A ${rx} ${ry} 0 1 0 ${cx - rx} ${cy} Z`;
    }

    let d = `M ${X(pts[0][0])} ${Y(pts[0][1])}`;
    for (let i = 1; i < pts.length; i++) d += ` L ${X(pts[i][0])} ${Y(pts[i][1])}`;
    return d + ' Z';
}

/**
 * Repousse hors du calque les points qui touchent son bord, de la portée du
 * flou. C'EST CE QUI EMPÊCHE L'ADOUCISSEMENT DE MANGER LE BORD DE L'IMAGE.
 *
 * Un fondu se centre sur la frontière qu'il adoucit. Quand cette frontière est
 * le bord du calque, la moitié du dégradé tombe sur des pixels qu'il faut
 * garder : mesuré au banc d'essai, un « effacer dehors » adouci de 4 mm laissait
 * l'alpha à 138/255 au ras du bord, et un « effacer dedans » y laissait un
 * résidu à 29 au lieu de 0. Une frange fantôme sur les quatre côtés.
 *
 * Laisser le geste dépasser (cf. `brider`) atténue le défaut sans le régler : la
 * correction dépendait alors de l'ampleur du dépassement, or la queue d'un flou
 * gaussien porte à trois écarts-types. On repousse donc explicitement de cette
 * distance-là, ce qui place le dégradé entièrement hors du calque.
 *
 * L'ELLIPSE EN EST EXCLUE, et l'avoir oubliée a coûté une régression mesurée.
 * Un rectangle, un lasso ou un polygone ont des côtés qui peuvent LONGER le bord
 * du calque : les repousser ne change rien de visible. Une ellipse, elle, est
 * définie par sa boîte englobante et ne touche ce bord qu'en quatre points de
 * tangence : dilater cette boîte gonfle toute la courbe. Sur une ellipse
 * inscrite dans le calque, « effacer dehors » laissait alors les coins à 54 au
 * lieu de 0 — c'est-à-dire ne les effaçait plus. Pour elle, le dépassement du
 * geste suffit, et une tangence n'est qu'un point.
 *
 * Rien ne se produit quand l'adoucissement est nul : sans dégradé, aucun bord à
 * protéger, et la forme est tracée exactement comme l'utilisateur l'a posée.
 */
function etendreAuxBords(pts, padU, padV) {
    const T = 0.002; // 0,18 mm sur un calque de 90 mm : imperceptible
    return pts.map(([u, v]) => [
        u <= T ? -padU : u >= 1 - T ? 1 + padU : u,
        v <= T ? -padV : v >= 1 - T ? 1 + padV : v,
    ]);
}

/** Trace la forme dans un contexte 2D, en pixels du masque. */
function tracerForme(ctx, marquee, w, h) {
    const pts = marquee.points || [];
    if (!pts.length) return;

    if (marquee.shape === 'rect' || marquee.shape === 'ellipse') {
        const [[u0, v0], [u1, v1]] = [pts[0], pts[pts.length - 1]];
        const x0 = u0 * w, y0 = v0 * h, x1 = u1 * w, y1 = v1 * h;
        const left = Math.min(x0, x1), top = Math.min(y0, y1);
        const bw = Math.abs(x1 - x0), bh = Math.abs(y1 - y0);
        if (marquee.shape === 'rect') {
            ctx.rect(left, top, bw, bh);
        } else {
            ctx.ellipse(left + bw / 2, top + bh / 2, bw / 2, bh / 2, 0, 0, Math.PI * 2);
        }
        return;
    }

    ctx.moveTo(pts[0][0] * w, pts[0][1] * h);
    for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0] * w, pts[i][1] * h);
    ctx.closePath();
}

/**
 * Applique la sélection au masque du calque et renvoie le PNG en data-URL.
 *
 * @param {object}  el        calque image
 * @param {object}  marquee   sélection, en coordonnées du masque
 * @param {number}  feather   adoucissement du bord, en millimètres
 * @param {boolean} exterieur effacer l'EXTÉRIEUR de la zone plutôt que
 *                            l'intérieur (recadrage non destructif)
 * @returns {Promise<string|null>} data-URL, ou null si le canvas est inutilisable
 */
export async function appliquerSelection(el, marquee, { feather = 0, exterieur = false } = {}) {
    const cvs = await openMaskCanvas(el);
    const ctx = cvs.getContext('2d');
    const w = cvs.width;
    const h = cvs.height;

    // Millimètres → pixels du masque. Le masque étant au ratio de la boîte,
    // la largeur suffit à établir l'échelle.
    const flou = feather > 0 ? (feather / Math.max(0.01, el.width || 1)) * w : 0;

    // Portée du dégradé : trois écarts-types. `ctx.filter = blur(r)` prend r
    // pour écart-type, et au-delà de 3 r la contribution est négligeable.
    const portee = 3 * flou;
    const forme = flou > 0 && marquee.shape !== 'ellipse'
        ? { ...marquee, points: etendreAuxBords(marquee.points || [], portee / w, portee / h) }
        : marquee;

    ctx.save();
    ctx.globalCompositeOperation = 'destination-out';
    // `ctx.filter` floute l'alpha de l'opération, donc le bord de l'effacement.
    // Absent de certains navigateurs anciens : on retombe alors sur un bord net
    // plutôt que de renoncer à l'effacement.
    if (flou > 0 && typeof ctx.filter === 'string') ctx.filter = `blur(${flou}px)`;
    ctx.fillStyle = 'rgba(0,0,0,1)';
    ctx.beginPath();

    if (exterieur) {
        // Tout sauf la forme, en règle « even-odd ». Le rectangle extérieur est
        // débordé de deux portées de flou, pour que sa propre frontière — qui
        // n'a aucun sens visuel — soit largement hors du calque.
        const m = 2 * portee + 2;
        ctx.rect(-m, -m, w + 2 * m, h + 2 * m);
        tracerForme(ctx, forme, w, h);
        ctx.fill('evenodd');
    } else {
        tracerForme(ctx, forme, w, h);
        ctx.fill();
    }
    ctx.restore();

    try {
        return cvs.toDataURL('image/png');
    } catch (_) {
        return null; // canvas taché (image distante sans CORS)
    }
}
